import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  user_id: string;
  user_name: string;
  user_type: 'trial' | 'pro';
  trial_times: number;
  used_times: number;
  created_at: string;
  role_id?: string | null;
  system_title?: string;
  workflow_main_title?: string;
  workflow_sub_title?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoggedIn: boolean;
  isAuthLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  checkTaskPermission: () => Promise<{ canExecute: boolean; message: string; remainingTimes: number }>;
  incrementUsage: () => Promise<void>;
  refreshUserInfo: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 带重试的 fetch 请求（网络丢包容错）
const fetchWithRetry = async (url: string, options: RequestInit = {}, maxRetries = 12): Promise<Response> => {
  let lastError: Error | null = null;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt < maxRetries) {
        const retryDelay = attempt * 2000;
        console.warn(`🔄 fetch 重试 (${attempt}/${maxRetries}): ${url}, ${retryDelay}ms 后重试, 错误: ${lastError.message}`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }
  throw lastError || new Error('请求失败');
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // 同步从LocalStorage初始化，避免首次渲染时 isLoggedIn=false 导致路由跳转
  const [user, setUser] = useState<User | null>(() => {
    try {
      const savedUserInfo = localStorage.getItem('userInfo');
      return savedUserInfo ? JSON.parse(savedUserInfo) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('authToken');
  });
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    return !!(localStorage.getItem('authToken') && localStorage.getItem('userInfo'));
  });
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(() => {
    // 如果有保存的token，需要异步验证，所以初始为loading
    return !!(localStorage.getItem('authToken') && localStorage.getItem('userInfo'));
  });

  // 初始化时异步验证token有效性
  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    if (savedToken) {
      verifyToken(savedToken);
    }
  }, []);

  // 验证token有效性
  const verifyToken = async (authToken: string) => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const response = await fetchWithRetry(`${apiBaseUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Token验证失败');
      }

      const data = await response.json();
      if (data.success && data.data.user) {
        setUser(data.data.user);
        localStorage.setItem('userInfo', JSON.stringify(data.data.user));
      } else {
        throw new Error('用户信息获取失败');
      }
    } catch (error) {
      console.error('Token验证失败:', error);
      logout();
    } finally {
      setIsAuthLoading(false);
    }
  };

  // 用户登录
  const login = async (username: string, password: string): Promise<{ success: boolean; message?: string }> => {
    console.log('AuthContext: 开始登录流程，用户名:', username);
    
    try {
      console.log('AuthContext: 发送登录请求到 /api/auth/login');
      console.log('AuthContext: 请求体:', { user_name: username, password: '***' });
      
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const response = await fetchWithRetry(`${apiBaseUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_name: username, password })
      });

      console.log('AuthContext: 收到响应，状态码:', response.status);
      console.log('AuthContext: 响应头:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        console.error('AuthContext: HTTP错误，状态码:', response.status, '状态文本:', response.statusText);
        
        try {
          // 尝试解析错误响应的JSON内容
          const errorData = await response.json();
          console.error('AuthContext: 错误响应数据:', errorData);
          
          // 如果后端返回了具体的错误信息，使用它
          if (errorData.message) {
            return { success: false, message: errorData.message };
          }
        } catch (parseError) {
          console.error('AuthContext: 无法解析错误响应为JSON:', parseError);
          // 如果无法解析JSON，尝试获取文本内容
          try {
            const errorText = await response.text();
            console.error('AuthContext: 错误响应文本:', errorText);
          } catch (textError) {
            console.error('AuthContext: 无法获取错误响应文本:', textError);
          }
        }
        
        // 如果无法获取具体错误信息，返回通用错误
        return { success: false, message: `服务器错误: ${response.status} ${response.statusText}` };
      }

      const data = await response.json();
      console.log('AuthContext: 解析的响应数据:', data);

      if (data.success && data.data) {
        const { token: authToken, user: userInfo } = data.data;
        console.log('AuthContext: 登录成功，用户信息:', userInfo);
        
        setToken(authToken);
        setUser(userInfo);
        setIsLoggedIn(true);
        
        // 保存到localStorage
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        
        return { success: true, message: data.message };
      } else {
        console.log('AuthContext: 登录失败，服务器返回:', data);
        return { success: false, message: data.message || '登录失败' };
      }
    } catch (error) {
      console.error('AuthContext: 登录请求异常:', error);
      // console.error('AuthContext: 错误详情:', {
      //   name: error?.name,
      //   message: error?.message,
      //   stack: error?.stack,
      //   cause: error?.cause
      // });
      
      // 检查是否是网络连接问题
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('AuthContext: 网络连接失败，可能是服务器未启动或CORS问题');
        return { success: false, message: '无法连接到服务器，请检查网络连接或联系管理员' };
      }
      
      return { success: false, message: '网络错误，请稍后重试' };
    }
  };

  // 用户登出
  const logout = () => {
    setUser(null);
    setToken(null);
    setIsLoggedIn(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
  };

  // 检查任务权限
  const checkTaskPermission = async (): Promise<{ canExecute: boolean; message: string; remainingTimes: number }> => {
    if (!token) {
      return { canExecute: false, message: '请先登录', remainingTimes: 0 };
    }

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const response = await fetchWithRetry(`${apiBaseUrl}/api/auth/check-permission`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success && data.data) {
        return {
          canExecute: data.data.canExecute,
          message: data.data.message,
          remainingTimes: data.data.remainingTimes
        };
      } else {
        return { canExecute: false, message: data.message || '权限检查失败', remainingTimes: 0 };
      }
    } catch (error) {
      console.error('权限检查失败:', error);
      return { canExecute: false, message: '网络错误', remainingTimes: 0 };
    }
  };

  // 增加使用次数
  const incrementUsage = async (): Promise<void> => {
    if (!token) return;

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const response = await fetchWithRetry(`${apiBaseUrl}/api/auth/increment-usage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success && user) {
        // 更新本地用户信息
        const updatedUser = { ...user, used_times: data.data.used_times };
        setUser(updatedUser);
        localStorage.setItem('userInfo', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('更新使用次数失败:', error);
    }
  };

  // 刷新用户信息
  const refreshUserInfo = async (): Promise<void> => {
    if (!token) return;

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const response = await fetchWithRetry(`${apiBaseUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success && data.data.user) {
        setUser(data.data.user);
        localStorage.setItem('userInfo', JSON.stringify(data.data.user));
      }
    } catch (error) {
      console.error('刷新用户信息失败:', error);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoggedIn,
    isAuthLoading,
    login,
    logout,
    checkTaskPermission,
    incrementUsage,
    refreshUserInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};